import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Button,
  Checkbox,
  Indicators,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Input,
  Textarea,
} from 'ui'
import { DistrictSelectFn, SchoolSelectFn } from '../FormControls'
import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { useUpdateProfileMutation } from '../../../queries/useUpdateProfileMutation'
import { useCurrentUser } from '../../../queries/useCurrentUser'
import { useAdminProfileById } from '../../../queries/useAdminProfileById'
import { useDistricts } from '../../../queries/useDistricts'
import { useSchools } from '../../../queries/useSchools'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { useSkipOnboarding } from '../../../hooks'
import { formatPhoneNumber } from 'utils'
import { QueryClient, useQueryClient } from '@tanstack/react-query'

// Validation schema
const schema = yup.object({
  districtId: yup.string().required('Cannot be blank'),
  schoolId: yup.string().optional(),
  firstName: yup.string().required('Cannot be blank'),
  lastName: yup.string().required('Cannot be blank'),
  phone: yup.string().min(10, 'Must be a valid phone number'),
  secondaryLanguages: yup
    .array()
    .of(yup.string().required())
    .optional()
    .nullable()
    .default([]),
  gradeLevels: yup.array().of(yup.string().required()).optional(),
  about: yup.string().optional().nullable().default(''),
})
export type EducatorProfilePart1 = yup.InferType<typeof schema>

interface EducatorStepOneProps {
  onSuccess?: () => void
  hideNavigation?: boolean
  editMode?: boolean
  profileId?: string // Added profileId parameter
  targetField?: string
}

const FormComponent: React.FC<EducatorStepOneProps> = ({
  onSuccess,
  hideNavigation = false,
  editMode = false,
  profileId,
  targetField,
}) => {
  // Use admin profile query if profileId is provided, otherwise use current user
  const { data: user, isLoading: isCurrentUserLoading } = useCurrentUser()
  const {
    data: adminProfile,
    isLoading: isAdminProfileLoading,
  } = useAdminProfileById(profileId)

  // Determine which profile data to use
  const profileData = profileId ? adminProfile : user
  const isLoading = profileId ? isAdminProfileLoading : isCurrentUserLoading
  const userId = profileId || (user?.userId as string)

  const navigate = useNavigate()
  const form = useForm<EducatorProfilePart1>({
    defaultValues: {
      districtId: '',
      firstName: '',
      lastName: '',
      phone: '',
      schoolId: '',
      secondaryLanguages: [],
      gradeLevels: [],
      about: '',
    } as EducatorProfilePart1,
    reValidateMode: 'onChange',
    resolver: yupResolver(schema as any) as any,
  })

  useEffect(() => {
    if (profileData) {
      form.setValue('schoolId', profileData?.schoolId)
      form.setValue('districtId', profileData?.districtId as string)
      form.setValue('firstName', profileData?.firstName as string)
      form.setValue('lastName', profileData?.lastName as string)
      form.setValue('phone', profileData?.phone as string)
      form.setValue('about', profileData?.about as string)
      form.setValue(
        'secondaryLanguages',
        profileData?.secondaryLanguages as string[]
      )
      form.setValue('gradeLevels', profileData?.gradeLevels as string[])
    }
  }, [profileData, form])

  const watchDistrictId = form.watch('districtId')

  const selectDistrict: DistrictSelectFn = (district) => {
    form.setValue('districtId', district.id)
    form.setValue('schoolId', '')
  }

  const selectSchool: SchoolSelectFn = (school) => {
    form.setValue('schoolId', school.id)
  }

  const { data: districts } = useDistricts()
  const { data: schools } = useSchools(watchDistrictId)

  const { mutateAsync: updateProfile } = useUpdateProfileMutation()
  const queryClient = useQueryClient()

  const handleSubmitForm = (redirect?: string) => async (
    values: EducatorProfilePart1
  ) => {
    await updateProfile({
      userId: userId,
      schoolId: values.schoolId as string,
      phone: values.phone as string,
      firstName: values.firstName as string,
      lastName: values.lastName as string,
      gradeLevels: (values.gradeLevels as string[]) ?? [],
      secondaryLanguages: (values.secondaryLanguages as string[]) ?? [],
      about: values.about as string,
    })
    queryClient.invalidateQueries({ queryKey: ['currentUser'] })

    // Call onSuccess callback if provided
    if (onSuccess) {
      onSuccess()
    }

    // Only navigate if redirect is provided and we're not in edit mode
    if (redirect && !editMode) {
      navigate(redirect)
    }
  }

  const skipOnboarding = useSkipOnboarding()

  const onSaveDraft = async () => {
    // Try to save current form data as best-effort, but always skip regardless of validation
    const values = form.getValues()
    try {
      await handleSubmitForm(undefined)(values)
    } catch {
      // Ignore errors - we're skipping anyway
    }
    skipOnboarding()
  }

  const languages = ['Spanish', 'French', 'Latin', 'German', 'Arabic', 'Other']
  const gradeLevels = ['K-2', '3-5', '6-8', '9-10', '11-12', 'Other']

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(
          handleSubmitForm(editMode ? undefined : '/onboard/educator/two')
        )}
        className={clsx(isLoading && 'opacity-50')}
      >
        {!editMode && (
          <FormLabel>Tell Us Who You Are and Where You Teach</FormLabel>
        )}

        <div className="flex flex-col sm:grid sm:grid-cols-4 md:grid-cols-12 gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem className="col-span-3 relative">
                <FormLabel className={editMode ? '' : 'hidden'}>
                  First Name
                </FormLabel>
                <FormControl>
                  <Input placeholder="First Name" {...field} />
                </FormControl>
                {targetField === 'firstName' && (
                  <div className="absolute -right-8 top-1/2 transform -translate-y-1/2">
                    <div className="w-4 h-4 bg-yellow-400 rounded-full animate-pulse"></div>
                  </div>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem className="col-span-6 relative">
                <FormLabel className={editMode ? '' : 'hidden'}>
                  Last Name
                </FormLabel>
                <FormControl>
                  <Input placeholder="Last Name" {...field} />
                </FormControl>
                {targetField === 'lastName' && (
                  <div className="absolute -right-8 top-1/2 transform -translate-y-1/2">
                    <div className="w-4 h-4 bg-yellow-400 rounded-full animate-pulse"></div>
                  </div>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem className="w-full col-span-3 relative">
                <FormLabel className={editMode ? '' : 'hidden'}>
                  Phone Number
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="Phone"
                    {...field}
                    onBlur={(e) => {
                      const formattedValue = formatPhoneNumber(e.target.value)
                      field.onChange(formattedValue)
                      field.onBlur()
                    }}
                  />
                </FormControl>
                {targetField === 'phone' && (
                  <div className="absolute -right-8 top-1/2 transform -translate-y-1/2">
                    <div className="w-4 h-4 bg-yellow-400 rounded-full animate-pulse"></div>
                  </div>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4 -mt-3">
          <FormField
            control={form.control}
            name="districtId"
            render={({ field }) => (
              <FormItem className="mt-6">
                <FormLabel className={editMode ? '' : 'hidden'}>
                  School District
                </FormLabel>
                <FormControl>
                  <Select
                    placeholder="Choose your district"
                    options={districts?.map((district) => ({
                      value: district,
                      label: district,
                    }))}
                    value={
                      districts
                        ?.filter(
                          (district) => (district as any) === field.value
                        )
                        .map((district) => ({
                          value: district,
                          label: district,
                        }))[0]
                    }
                    onChange={(option) => {
                      field.onChange(option?.value)
                      if (option) {
                        selectDistrict({
                          id: option.value,
                          name: option.label,
                        })
                      }
                    }}
                    classNames={{
                      control: () =>
                        'flex w-full rounded-md border border-input bg-background py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                    }}
                    isDisabled={form.formState.isSubmitting}
                    isClearable
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="schoolId"
            render={({ field }) => (
              <FormItem className="mt-6">
                <FormLabel className={editMode ? '' : 'hidden'}>
                  School
                </FormLabel>
                <FormControl>
                  <Select
                    placeholder="Choose your school"
                    options={schools?.map((school) => ({
                      value: school.id,
                      label: school.name,
                    }))}
                    value={
                      field.value?.length
                        ? schools
                            ?.filter((school) => school.id === field.value)
                            .map((school) => ({
                              value: school.id,
                              label: school.name,
                            }))[0]
                        : null
                    }
                    onChange={(option) => {
                      field.onChange(option?.value)
                      if (option) {
                        selectSchool({
                          id: option.value,
                          name: option.label,
                        })
                      }
                    }}
                    classNames={{
                      control: () =>
                        'flex w-full rounded-md border border-input bg-background py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                    }}
                    isDisabled={form.formState.isSubmitting}
                    isClearable
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex flex-col mt-8">
          <FormLabel>What Grade Level(s) Do You Teach</FormLabel>
          <p className="font-body text-sm mt-1">Choose all that apply.</p>
          <FormField
            control={form.control}
            name="gradeLevels"
            render={({ field: { value, onChange } }) => (
              <div className="grid grid-cols-8 gap-2 mt-4">
                {gradeLevels.map((language) => (
                  <FormItem key={language} className="col-span-3">
                    <FormControl>
                      <Checkbox
                        checked={value?.includes(language)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            onChange([...(value ?? []), language])
                          } else {
                            onChange(value?.filter((l) => l !== language))
                          }
                        }}
                      />
                    </FormControl>
                    <FormLabel className="text-sm ml-2 text-body">
                      {language}
                    </FormLabel>
                  </FormItem>
                ))}
              </div>
            )}
          />
        </div>

        <div className="flex flex-col mt-8">
          <FormLabel>
            Are Your Students Fluent in Languages Besides English?
          </FormLabel>
          <p className="font-body text-sm mt-1">
            In other words, would you teach an entire class primarily in a
            non-English language? Choose all that apply.
          </p>
          <FormField
            control={form.control}
            name="secondaryLanguages"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="hidden">Secondary Languages</FormLabel>
                <FormControl>
                  <CreatableSelect
                    isDisabled={form.formState.isSubmitting}
                    isMulti
                    isClearable={false}
                    placeholder="Select or create languages"
                    options={languages.map((language) => ({
                      value: language,
                      label: language,
                    }))}
                    value={
                      field.value?.map((language) => ({
                        value: language,
                        label: language,
                      })) || []
                    }
                    onChange={(selectedOptions) => {
                      field.onChange(
                        selectedOptions.map((option) => option.value)
                      )
                    }}
                    onCreateOption={(inputValue) => {
                      field.onChange([...(field.value || []), inputValue])
                    }}
                    classNames={{
                      control: () =>
                        'flex w-full rounded-md border border-input bg-background py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <p className="text-xs font-body text-body/70 md:mr-28 mt-1">
            When you start typing, we'll suggest options that are already in our
            system. You can also create new language options if needed.
          </p>
        </div>

        <div className="flex flex-col mt-8">
          <FormLabel className="mt-8">
            {editMode
              ? 'About Yourself'
              : 'Optional: Tell Us a Little About Yourself'}
          </FormLabel>
          {!editMode && (
            <>
              <p className="font-body text-sm mt-3">
                Tell us in plain English about you and the life experiences that
                you bring to the classroom. We'll use this info to write a
                starter bio that you'll be free to edit.
              </p>
              <p className="font-body text-sm text-body/70 my-3">
                EX: I've been a teacher since 2006 with a focus on making
                learning explorational. Outside of the classroom, I'm an avid
                hiker who sometimes dabbles in clarinet. I'm often described as
                throughtful and compassionate.
              </p>
            </>
          )}
          <FormField
            control={form.control}
            name="about"
            render={({ field }) => (
              <FormItem className="w-full col-span-3 relative">
                <FormLabel className="hidden">About</FormLabel>
                <FormControl>
                  <Textarea {...field} />
                </FormControl>
                {targetField === 'about' && (
                  <div className="absolute -right-8 top-1/2 transform -translate-y-1/2">
                    <div className="w-4 h-4 bg-yellow-400 rounded-full animate-pulse"></div>
                  </div>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {!hideNavigation ? (
          <div className="flex-col flex mt-12 w-full">
            <Button
              type="submit"
              disabled={form.formState.isSubmitting}
              variant="default"
              className="mx-auto uppercase"
            >
              {form.formState.isSubmitting ? (
                <Indicators.Spinner
                  size="lg"
                  className="mt-auto mr-2 mb-auto ml-auto"
                />
              ) : null}
              <span>Next: Subjects &amp; Interests &gt;</span>
            </Button>
            <Button
              type="button"
              variant="link"
              className="text-actionable underline text-sm"
              onClick={onSaveDraft}
            >
              <span>Save Draft and Finish Later</span>
            </Button>
          </div>
        ) : (
          <div className="flex-col flex mt-8 w-full">
            <Button
              type="submit"
              disabled={form.formState.isSubmitting}
              variant="default"
              className="ml-auto"
            >
              {form.formState.isSubmitting ? (
                <Indicators.Spinner
                  size="lg"
                  className="mt-auto mr-2 mb-auto ml-auto"
                />
              ) : null}
              <span>{editMode ? 'Update Profile' : 'Save Changes'}</span>
            </Button>
          </div>
        )}
      </form>
    </Form>
  )
}

export const EducatorStepOne = FormComponent
